import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, ADMIN_TOKEN_KEY } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, Loader2 } from 'lucide-react';

export default function AdminLoginPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await adminApi.post('/admin/login', { email: email.trim(), password });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.access_token);
      toast.success('Welcome, admin.');
      nav('/admin', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-black to-red-950/40 px-4 py-16 text-white">
      <div className="mx-auto max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2">
          <ShieldCheck className="h-7 w-7 text-red-500" />
          <div className="font-display text-2xl font-bold tracking-tight">StudyTrack AI · Admin</div>
        </div>
        <Card className="border-red-500/20 bg-neutral-950/80 backdrop-blur">
          <CardContent className="space-y-5 p-6 sm:p-8">
            <div>
              <div className="font-display text-xl font-semibold">Admin sign-in</div>
              <div className="mt-1 text-sm text-neutral-400">Restricted area — StudyTrack AI operators only.</div>
            </div>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
              <div className="space-y-1.5">
                <Label htmlFor="a-email">Admin email</Label>
                <Input
                  id="a-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@studytrackai.in"
                  required
                  autoFocus
                  data-testid="admin-email-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="a-password">Password</Label>
                <Input
                  id="a-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-password-input"
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="h-11 w-full bg-red-600 font-semibold hover:bg-red-500"
                data-testid="admin-login-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
